<template>
  <div>
    <!-- Bulk Upload Sidebar -->
    <b-sidebar
      id="sidebar-bulk-upload"
      bg-variant="white"
      v-model="sidebarVisible"
      sidebar-class="sidebar-lg"
      right
      no-header
      backdrop
      shadow
    >
      <!-- @hidden="onBulkSidebarHidden" -->
      <div
        class="d-flex justify-content-between align-items-center content-sidebar-header px-2 py-1"
      >
        <h5 class="mb-0">{{ $t("Bulk Upload Branches") }}</h5>
        <feather-icon
          class="ml-1 cursor-pointer"
          icon="XIcon"
          size="16"
          @click="sidebarVisible = false"
        />
      </div>

      <div class="p-2">
        <!-- Download Sample Button -->
        <b-button variant="outline-secondary" block @click="downloadSample">
          <feather-icon icon="DownloadIcon" class="mr-50" />
          {{ $t("Download Sample File") }}
        </b-button>

        <hr class="my-2" />

        <!-- Upload Excel File Button -->
        <b-button
          variant="primary"
          block
          @click="$refs.bulkFileInput.click()"
          :disabled="importLoading || !!jobID"
        >
          <feather-icon icon="UploadIcon" class="mr-50" />
          {{ $t("Upload Excel File") }}
        </b-button>
        <input
          ref="bulkFileInput"
          type="file"
          accept=".xlsx,.xls"
          style="display: none"
          @change="handleFileUpload"
        />

        <!-- Record count badge -->
        <div
          v-if="importData.length > 0"
          class="mt-2 d-flex align-items-center"
        >
          <b-badge variant="success" style="font-size: 14px; padding: 8px 16px">
            {{ importData.length }} {{ $t("records read from Excel") }}
          </b-badge>
          <b-button
            variant="outline-danger"
            size="sm"
            @click="importData = []"
            v-b-tooltip.hover.top
            :title="$t('Clear')"
            class="btn-icon ml-1"
            :disabled="!!jobID"
          >
            <feather-icon icon="XIcon" />
          </b-button>
        </div>

        <!-- Import Now Button -->
        <b-button
          v-if="importData.length > 0 && !jobID"
          variant="success"
          block
          class="mt-2"
          @click="importNow"
          :disabled="importLoading"
        >
          <b-spinner v-if="importLoading" small type="grow" class="mr-50" />
          <feather-icon v-else icon="CheckIcon" class="mr-50" />
          {{ $t("Import Now") }}
        </b-button>

        <!-- Bulk import job progress -->
        <div v-if="jobID" class="mt-2">
          <div class="font-weight-bold mb-50">
            {{
              jobStatus && jobStatus.percent >= 100
                ? $t("Completed")
                : $t("Processing...")
            }}
          </div>
          <b-progress
            v-if="jobStatus"
            v-model="jobProgressPercent"
            max="100"
            height="10px"
            :variant="jobProgressVariant"
            class="mb-50"
            show-progress
            animated
          />
          <div v-else class="small text-muted mb-50">
            {{ $t("Starting background job...") }}
          </div>
          <div v-if="jobStatus" class="small">
            {{ $t("Processed") }} {{ jobStatus.processed || 0 }} {{ $t("of") }}
            {{ jobStatus.total || 0 }}
            <span v-if="jobStatus.percent != null">
              ({{ Math.round(jobStatus.percent) }}%)
            </span>
          </div>
          <div
            v-if="jobStatus && jobStatus.error"
            class="small text-danger mt-50"
          >
            {{ jobStatus.error }}
          </div>
        </div>
      </div>
    </b-sidebar>

    <!-- select 2 demo -->
    <div class="branch-cards-page">
      <b-container fluid class="px-2 px-md-3">
        <div class="branch-toolbar-card">
          <div class="branch-toolbar__actions">
            <button
              v-if="rights.add && canManageBranches"
              type="button"
              class="branch-add-btn"
              @click="Form()"
            >
              <span class="branch-add-btn__icon">
                <feather-icon icon="PlusIcon" size="18" />
              </span>
              <span>{{ $t("Add Branch") }}</span>
            </button>
            <b-button
              v-if="canViewCompanies"
              :to="{ name: 'manage-companies' }"
              variant="light"
              class="branch-secondary-btn"
            >
              <feather-icon icon="BriefcaseIcon" class="mr-50" size="16" />
              <span class="align-middle">{{ $t("Companies") }}</span>
            </b-button>
            <b-button
              v-if="rights.add && isTcfDomain && canManageBranches"
              @click="sidebarVisible = true"
              variant="light"
              class="branch-secondary-btn"
            >
              <feather-icon icon="UploadIcon" class="mr-50" size="16" />
              <span class="align-middle">{{ $t("Bulk Upload") }}</span>
            </b-button>
          </div>
          <div class="branch-search-field">
            <feather-icon
              icon="SearchIcon"
              size="18"
              class="branch-search-field__icon"
            />
            <input
              v-model="searchQuery"
              type="search"
              class="branch-search-field__input"
              :placeholder="$t('Search campuses...')"
            />
          </div>
        </div>

        <div class="branch-page-intro">
          <span class="branch-page-intro__icon">
            <feather-icon icon="HomeIcon" size="22" />
          </span>
          <div class="branch-page-intro__text">
            <h4 class="branch-page-intro__title mb-0">{{ $t("Campuses") }}</h4>
            <p class="branch-page-intro__sub mb-0">
              {{ $t("Branch profiles, contact info, and cover images") }}
            </p>
          </div>
          <b-badge
            v-if="!dataLoading"
            variant="light-primary"
            pill
            class="branch-page-intro__count"
          >
            {{ campusCountBadge }}
          </b-badge>
        </div>

        <div v-if="dataLoading" class="branch-grid">
          <div
            v-for="n in skeletonCount"
            :key="'sk-' + n"
            class="branch-grid__col"
          >
            <div class="campus-card campus-card--skeleton">
              <div class="campus-card__cover-wrap">
                <div class="skeleton-block campus-card__cover skeleton-block--flat" />
              </div>
              <div class="campus-card__body">
                <div class="campus-card__header">
                  <div class="campus-card__logo-wrap">
                    <div class="skeleton-block skeleton-block--logo" />
                  </div>
                  <div class="campus-card__titles campus-card__titles--skeleton">
                    <div class="skeleton-line skeleton-line--title" />
                    <div class="skeleton-line skeleton-line--sub" />
                  </div>
                </div>
                <ul class="campus-card__meta">
                  <li v-for="m in 4" :key="'sk-meta-' + n + '-' + m">
                    <div class="skeleton-block skeleton-block--icon" />
                    <div class="skeleton-meta-col">
                      <div class="skeleton-line skeleton-line--label" />
                      <div class="skeleton-line skeleton-line--value" />
                    </div>
                  </li>
                </ul>
                <div class="campus-card__footer">
                  <div class="skeleton-block skeleton-block--edit" />
                </div>
              </div>
            </div>
          </div>
        </div>

        <div
          v-else-if="showEmptySearch"
          class="branch-empty"
        >
          <feather-icon icon="SearchIcon" size="40" class="text-muted mb-1" />
          <h5 class="mb-0">{{ $t("No campuses match your search") }}</h5>
          <b-button
            size="sm"
            variant="outline-primary"
            class="mt-1"
            @click="searchQuery = ''"
          >
            {{ $t("Clear search") }}
          </b-button>
        </div>

        <div
          v-else-if="showEmptyList"
          class="branch-empty"
        >
          <feather-icon icon="InboxIcon" size="40" class="text-muted mb-1" />
          <h5 class="mb-0">{{ $t("No records found") }}</h5>
          <small v-if="loadError" class="text-muted mt-50 d-block">{{
            $t("Could not load campuses. Check your connection and try again.")
          }}</small>
          <b-button
            size="sm"
            variant="outline-primary"
            class="mt-1"
            @click="LoadData()"
          >
            {{ $t("Retry") }}
          </b-button>
        </div>

        <div v-else class="branch-grid">
          <div
            v-for="c in filters"
            :key="c.id"
            class="branch-grid__col"
          >
            <article
              class="campus-card"
              :class="{
                'campus-card--clickable':
                  rights.edit && canManageBranches,
              }"
              @click="onCardClick(c)"
            >
              <div class="campus-card__cover-wrap">
                <img
                  class="campus-card__cover"
                  :src="campusCoverUrl(c)"
                  :alt="c.branch || c.name"
                  loading="lazy"
                  @error="onCoverImageError($event, c)"
                />
                <div class="campus-card__cover-shade" />
                <span
                  v-if="campusCodeDisplay(c)"
                  class="campus-card__code-badge"
                >
                  {{ campusCodeDisplay(c) }}
                </span>
              </div>

              <div class="campus-card__body">
                <div class="campus-card__header">
                  <div class="campus-card__logo-wrap">
                    <img
                      v-if="campusLogoUrl(c) && !logoLoadFailed[c.id]"
                      class="campus-card__logo-img"
                      :src="campusLogoUrl(c)"
                      :alt="c.branch"
                      loading="lazy"
                      @error="onLogoImageError(c.id)"
                    />
                    <span v-else class="campus-card__logo-fallback">
                      {{ campusInitials(c) }}
                    </span>
                  </div>
                  <div class="campus-card__titles">
                    <h3 class="campus-card__branch">{{ c.branch }}</h3>
                    <p v-if="c.name" class="campus-card__org">{{ c.name }}</p>
                  </div>
                </div>

                <ul class="campus-card__meta">
                  <li v-if="c.mobile">
                    <span class="campus-card__meta-icon">
                      <feather-icon icon="PhoneIcon" size="15" />
                    </span>
                    <div class="campus-card__meta-text">
                      <span class="campus-card__meta-label">{{
                        $t("Phone")
                      }}</span>
                      <span class="campus-card__meta-value">{{ c.mobile }}</span>
                    </div>
                  </li>
                  <li v-if="c.email">
                    <span class="campus-card__meta-icon">
                      <feather-icon icon="MailIcon" size="15" />
                    </span>
                    <div class="campus-card__meta-text">
                      <span class="campus-card__meta-label">{{
                        $t("Email")
                      }}</span>
                      <span class="campus-card__meta-value">{{ c.email }}</span>
                    </div>
                  </li>
                  <li v-if="c.website">
                    <span class="campus-card__meta-icon">
                      <feather-icon icon="GlobeIcon" size="15" />
                    </span>
                    <div class="campus-card__meta-text">
                      <span class="campus-card__meta-label">{{
                        $t("Website")
                      }}</span>
                      <span class="campus-card__meta-value">{{
                        c.website
                      }}</span>
                    </div>
                  </li>
                  <li v-if="canViewCompanies && campusCompanyLabel(c)">
                    <span class="campus-card__meta-icon">
                      <feather-icon icon="BriefcaseIcon" size="15" />
                    </span>
                    <div class="campus-card__meta-text">
                      <span class="campus-card__meta-label">{{
                        $t("Companies")
                      }}</span>
                      <span class="campus-card__meta-value">{{
                        campusCompanyLabel(c)
                      }}</span>
                    </div>
                  </li>
                  <li v-if="c.address">
                    <span class="campus-card__meta-icon">
                      <feather-icon icon="MapPinIcon" size="15" />
                    </span>
                    <div class="campus-card__meta-text">
                      <span class="campus-card__meta-label">{{
                        $t("Address")
                      }}</span>
                      <span class="campus-card__meta-value">{{
                        c.address
                      }}</span>
                    </div>
                  </li>
                </ul>

                <div class="campus-card__footer">
                        <b-button
                          v-if="rights.edit && canManageBranches"
                          @click.stop="Edit(c)"
                          v-b-tooltip.hover.top
                          :title="$t('Edit')"
                          v-ripple.400="'rgba(113, 102, 240, 0.15)'"
                          variant="outline-primary"
                    size="sm"
                    class="btn-icon rounded-circle campus-card__edit"
                        >
                    <feather-icon icon="EditIcon" size="16" />
                        </b-button>
                      </div>
              </div>
            </article>
          </div>
        </div>
      </b-container>
    </div>
  </div>
</template>

<script>
import {
  BCard,
  BRow,
  BCol,
  BButton,
  BTable,
  BMedia,
  BAvatar,
  BLink,
  BBadge,
  BDropdown,
  BDropdownItem,
  BPagination,
  BImg,
  VBTooltip,
  BFormCheckbox,
  BFormGroup,
  BForm,
  BContainer,
  BMediaAside,
  BAside,
  BSpinner,
  BCardTitle,
  BCardText,
  BInputGroupPrepend,
  BFormInput,
  BInputGroup,
  BSidebar,
  BProgress,
} from "bootstrap-vue";
import Ripple from "vue-ripple-directive";
import vSelect from "vue-select";
import store from "@/store";
import * as XLSX from "xlsx";
//import { ref, onUnmounted } from '@vue/composition-api'
import { avatarText } from "@core/utils/filter";
//import useAllOrdersList from './useAllOrdersList'
//import orderStoreModule from '../allordersStoreModule'
import flatPickr from "vue-flatpickr-component";
import { BFormSelect } from "bootstrap-vue";
//import Multiselect from "vue-multiselect";
import VueGallery from "vue-gallery";
import { mapActions, mapGetters } from "vuex";
import { parseCampusProgramsFromExcelRow } from "@/views/myskool/schoolinfo/campusProgramOptions";
import {
  companyIdsLabel,
} from "@/utils/companyIds";

export default {
  components: {
    //Multiselect,

    BCard,
    BRow,
    BCol,
    //BAvatar,
    BFormInput,
    BButton,
    BFormGroup,
    //BFormGroup,
    // BFormCheckbox,
    //BForm,
    BContainer,
    //BCardTitle,
    BCardText,
    BInputGroupPrepend,
    BInputGroup,
    BSidebar,
    BBadge,
    BProgress,
    //BMediaAside,
    //BImg ,
    // BMedia,
    //BAvatar,
    //BLink,
    BSpinner,
    //BBadge,
    //BDropdown,
    //BDropdownItem,
    //BPagination,
    //vSelect,
    //flatPickr,
    //'gallery': VueGallery
  },
  props: {},
  directives: {
    "b-tooltip": VBTooltip,
    Ripple,
  },
  async created() {
    let right = {};
    this.$store.state.menu.forEach((el) => {
      el.children.forEach((ch) => {
        right[ch.route] = {
          view: ch.view,
          add: ch.add,
          edit: ch.edit,
          delete: ch.delete,
        };
      });
    });
    this.$store.commit("setRights", right);
    // console.log(this.$store.state.rights);

    if (!this.$store.state.rights[this.$route.name]) {
      // console.log(this.$store.state.rights[this.$route.name]);
      this.$router.replace({
        name: "misc-not-authorized",
      });
    } else {
      this.rights = this.$store.state.rights[this.$route.name];
    }
    await this.LoadSettings();
    await this.LoadData();
  },
  activated() {
    if (this.$store.state.rights[this.$route.name]) {
      this.LoadData();
    }
  },
  computed: {
    isTcfDomain() {
      return ["myskooltcfdemo", "myskooltcf"].includes(this.reportDomain);
    },
    isIussDomain() {
      return this.reportDomain === "myskooliuss";
    },
    companyRights() {
      return this.$store.state.rights["manage-companies"] || {};
    },
    canViewCompanies() {
      return this.isIussDomain && !!this.companyRights.view;
    },
    canManageBranches() {
      if (this.isTcfDomain) {
        return !!this.isHeadOffice;
      }
      return true;
    },
    filters: function () {
      const q = (this.searchQuery || "").toLowerCase().trim();
      if (!q) return this.items;
      return this.items.filter((pro) => {
        const branch = String(pro.branch || "").toLowerCase();
        const name = String(pro.name || "").toLowerCase();
        const code = String(pro.campusCode || "").toLowerCase();
        return (
          branch.includes(q) || name.includes(q) || code.includes(q)
        );
      });
    },
    hasSearchQuery() {
      return !!(this.searchQuery || "").trim();
    },
    showEmptySearch() {
      return (
        this.items.length > 0 &&
        this.filters.length === 0 &&
        this.hasSearchQuery
      );
    },
    showEmptyList() {
      return this.items.length === 0;
    },
    campusCountBadge() {
      const n = this.filters.length;
      const word = n === 1 ? this.$t("campus") : this.$t("campuses");
      if (this.hasSearchQuery && this.items.length !== n) {
        return `${n} ${word} · ${this.$t("of")} ${this.items.length}`;
      }
      return `${n} ${word}`;
    },
    jobProgressPercent() {
      if (!this.jobStatus || this.jobStatus.percent == null) return 0;
      const p = Number(this.jobStatus.percent);
      if (Number.isNaN(p)) return 0;
      return Math.min(100, Math.max(0, p));
    },
    jobProgressVariant() {
      if (!this.jobStatus) return "primary";
      if (this.jobStatus.error) return "danger";
      if (this.jobProgressPercent >= 100) return "success";
      return "primary";
    },
  },
  data() {
    return {
      rights: {},
      searchQuery: "",
      dataLoading: false,
      loadError: false,
      campusLoadSeq: 0,
      items: [],
      limit: 0,
      sidebarVisible: false,
      importData: [],
      importLoading: false,
      jobID: null,
      jobStatus: null,
      pollTimer: null,
      isHeadOffice: null,
      reportDomain: this.$store.state.report.replace(/_/g, ""),
      skeletonCount: 8,
      logoLoadFailed: {},
      defaultCampusCover:
        "data:image/svg+xml," +
        encodeURIComponent(
          '<svg xmlns="http://www.w3.org/2000/svg" width="480" height="160" viewBox="0 0 480 160">' +
            '<defs><linearGradient id="g" x1="0%" y1="0%" x2="100%" y2="100%">' +
            '<stop offset="0%" stop-color="#eef0f8"/><stop offset="45%" stop-color="#d8dcf0"/><stop offset="100%" stop-color="#b8bfd9"/>' +
            "</linearGradient></defs>" +
            '<rect width="480" height="160" fill="url(#g)"/>' +
            '<circle cx="400" cy="28" r="72" fill="#ffffff" opacity="0.35"/>' +
            '<circle cx="60" cy="140" r="56" fill="#ffffff" opacity="0.28"/>' +
            '<circle cx="240" cy="80" r="40" fill="#7367f0" opacity="0.08"/>' +
            "</svg>"
        ),
      companiesById: {},
    };
  },
  beforeDestroy() {
    this.stopPolling();
  },
  methods: {
    ...mapActions({ get: "get", post: "post", put: "put", delete: "delete" }),

    normalizeCampusList(response) {
      if (Array.isArray(response)) return response;
      if (response && Array.isArray(response.data)) return response.data;
      if (response && response.data && Array.isArray(response.data.data)) {
        return response.data.data;
      }
      return [];
    },
    sortHeadOfficeFirst(list) {
      const rows = Array.isArray(list) ? [...list] : [];
      const headOfficeIndex = rows.findIndex(
        (campus) =>
          String(campus && campus.branch ? campus.branch : "")
            .toLowerCase()
            .trim() === "head office"
      );
      if (headOfficeIndex > 0) {
        const headOffice = rows.splice(headOfficeIndex, 1)[0];
        rows.unshift(headOffice);
      }
      return rows;
    },
    applyCampusItems(list) {
      const sorted = this.sortHeadOfficeFirst(list);
      this.$store.commit("setBranches", sorted);
      const branch = sorted.find(
        (el) => el.id == this.$store.state.userData.cId
      );
      if (branch) {
        this.$store.commit("setBranchName", branch.branch);
        this.$store.commit("setCurrentBranch", branch);
      }
      if (sorted.length && sorted[0].limit != null) {
        this.limit = sorted[0].limit;
      }

      if (this.isTcfDomain && !this.isHeadOffice) {
        this.items = sorted.filter(
          (campus) => campus.id == this.$store.state.userData.cId
        );
      } else {
        this.items = sorted;
      }
    },
    async fetchCampusListFromApi() {
      const obj = {
        url:
          this.$store.state.domain +
          "campuses?db=" +
          this.$store.state.userData.db +
          "&cId=" +
          this.$store.state.userData.cId,
        token: this.$store.state.userData.token,
      };
      const response = await this.get(obj);
      return this.normalizeCampusList(response);
    },

    onCardClick(c) {
      if (this.rights.edit && this.canManageBranches) {
        this.Edit(c);
      }
    },
    campusCoverUrl(campus) {
      if (!campus) return this.defaultCampusCover;
      const raw =
        campus.cover ||
        campus.coverImage ||
        campus.coverPic ||
        campus.coverPhoto ||
        campus.banner ||
        campus.bannerImage ||
        campus.backgroundImage;
      const url = raw != null ? String(raw).trim() : "";
      return url || this.defaultCampusCover;
    },
    campusLogoUrl(campus) {
      if (!campus) return null;
      const raw = campus.logo;
      const url = raw != null ? String(raw).trim() : "";
      return url || null;
    },
    campusInitials(campus) {
      const source = String(campus?.branch || campus?.name || "?").trim();
      const parts = source.split(/\s+/).filter(Boolean);
      if (!parts.length) return "?";
      if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
      return (parts[0][0] + parts[1][0]).toUpperCase();
    },
    onCoverImageError(event) {
      if (event && event.target) {
        event.target.onerror = null;
        event.target.src = this.defaultCampusCover;
      }
    },
    onLogoImageError(campusId) {
      this.$set(this.logoLoadFailed, campusId, true);
    },
    campusCompanyLabel(campus) {
      if (!campus || !campus.companyIDs) return "";
      return companyIdsLabel(campus.companyIDs, this.companiesById);
    },
    campusCodeDisplay(campus) {
      const code = String(campus?.campusCode || "").trim();
      return code || "";
    },
    async loadCompaniesMap() {
      if (!this.canViewCompanies) {
        this.companiesById = {};
        return;
      }
      try {
        const rows = await this.get({
          url:
            this.$store.state.domain +
            "Companies?db=" +
            encodeURIComponent(this.$store.state.userData.db) +
            "&cID=" +
            this.$store.state.userData.cId,
          token: this.$store.state.userData.token,
        });
        const list = Array.isArray(rows) ? rows : [];
        const map = {};
        list.forEach((c) => {
          map[c.id] = c;
        });
        this.companiesById = map;
      } catch (e) {
        this.companiesById = {};
      }
    },

    Form() {
      if (["myskooltcfdemo", "myskooltcf"].includes(this.reportDomain)) {
        this.$router.push({
          name: "campus-details",
          params: { id: 0 },
        });
      } else if (this.limit == this.items.length) {
        this.$bvToast.toast(this.$t("Contact MySkool to add more campuses."), {
          title: this.$t("Error") + "!",
          variant: "danger",
          toaster: "b-toaster-top-center",
        });
      } else this.$router.push("/manage-addbranch");
    },
    Edit(row) {
      if (["myskooltcfdemo", "myskooltcf"].includes(this.reportDomain)) {
        this.$router.push({
          name: "campus-details",
          params: { id: String(row.id) },
        });
      } else {
        this.$router.push({
          name: "manage-addbranch",
          params: { row: { row } },
        });
      }
    },
    async Delete(id) {
      let result = await Swal.fire({
        title: this.$t("Are you sure?"),
        text: this.$t("You won't be able to revert this!"),
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#d33",
        cancelButtonColor: "#3085d6",
        confirmButtonText: this.$t("Yes, Delete it!"),
      });

      if (result.isConfirmed) {
        var status = await this.delete({
          url:
            this.$store.state.domain +
            "campuses/" +
            id +
            "?db=" +
            this.$store.state.userData.db +
            "&cId=" +
            this.$store.state.userData.cId,
          message: "Campus deleted successfully.",
          context: this,
          body: null,
          token: this.$store.state.userData.token,
        });
        if (status) this.LoadData();
      }
    },

    downloadSample() {
      const link = document.createElement("a");
      link.href = "/institute-profile-sample.xlsx";
      link.download = "institute-profile-sample.xlsx";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    },

    handleFileUpload(event) {
      const file = event.target.files[0];
      if (!file) return;

      const reader = new FileReader();
      reader.onload = (e) => {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: "array" });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        const rows = XLSX.utils.sheet_to_json(worksheet, { defval: "" });

        const getVal = (row, ...keys) => {
          for (const key of keys) {
            const found = Object.keys(row).find(
              (k) => k.trim().toLowerCase() === key.trim().toLowerCase()
            );
            if (
              found !== undefined &&
              row[found] !== undefined &&
              row[found] !== ""
            ) {
              return row[found];
            }
          }
          return "";
        };

        const toNum = (val) => {
          const n = parseInt(val);
          return isNaN(n) ? 0 : n;
        };

        this.importData = rows
          .map((row) => {
            const programs = parseCampusProgramsFromExcelRow(getVal, row);

            return {
              id: 0,
              campusID: 0,
              instituteName: String(
                getVal(row, "Institute Name", "instituteName", "Institute")
              ),
              branch: String(getVal(row, "Branch", "branch")),
              yearOfEstablishment: toNum(
                getVal(
                  row,
                  "Year of Establishment",
                  "yearOfEstablishment",
                  "Year"
                )
              ),
              branchCode: String(
                getVal(row, "Branch Code", "branchCode", "Branch code")
              ),
              address: String(
                getVal(row, "Address", "address", "Institute Location")
              ),
              primaryContact: String(
                getVal(
                  row,
                  "Primary Contact",
                  "primaryContact",
                  "Primary Contact Number"
                )
              ),
              whatsappNumber: String(
                getVal(
                  row,
                  "WhatsApp Number",
                  "Whatsapp Number",
                  "whatsappNumber",
                  "WhatsApp"
                )
              ),
              email: String(
                getVal(
                  row,
                  "Email",
                  "email",
                  "Institute Email",
                  "Official Email Address"
                )
              ),
              pocName: String(getVal(row, "POC Name", "pocName", "POC")),
              pocDesignation: String(
                getVal(row, "POC Designation", "pocDesignation", "Designation")
              ),
              pocContactNumber: String(
                getVal(
                  row,
                  "POC Contact Number",
                  "pocContactNumber",
                  "POC Contact"
                )
              ),
              pocEmail: String(
                getVal(row, "POC Email", "pocEmail", "POC Email Address")
              ),
              pocAddress: String(getVal(row, "POC Address", "pocAddress")),
              pocCnic: String(getVal(row, "POC CNIC", "pocCnic")),
              pocPicture: String(getVal(row, "POC Picture", "pocPicture")),
              programsOffered: programs.join(","),
              studentCapacity: toNum(
                getVal(
                  row,
                  "Student Capacity",
                  "studentCapacity",
                  "Total Student Capacity"
                )
              ),
              avgClassSize: toNum(
                getVal(
                  row,
                  "Avg Class Size",
                  "avgClassSize",
                  "Average Class Size"
                )
              ),
              monthlyFeePerStudent: toNum(
                getVal(
                  row,
                  "Monthly Fee Per Student",
                  "monthlyFeePerStudent",
                  "Monthly Fee/Student"
                )
              ),
              monthlyFeeForHeadOffice: toNum(
                getVal(
                  row,
                  "Monthly Fee For Head Office",
                  "monthlyFeeForHeadOffice",
                  "Monthly Fee for Head Office",
                  "Monthly Fee for TCF Students",
                  "Monthly Fee for TCF Student"
                )
              ),
              bankName: String(
                getVal(row, "Bank Name", "bankName", "Institute Bank Name")
              ),
              bankAccountNumber: String(
                getVal(
                  row,
                  "Bank Account Number",
                  "bankAccountNumber",
                  "Account Number",
                  "Institute Account Number",
                  "Institute Bank Account Number"
                )
              ),
              bankAccountTitle: String(
                getVal(
                  row,
                  "Bank Account Title",
                  "bankAccountTitle",
                  "Account Title",
                  "Institute Account Title",
                  "Institute Bank Account Title"
                )
              ),
              bankAccountBranch: String(
                getVal(
                  row,
                  "Bank Account Branch",
                  "bankAccountBranch",
                  "Account Branch",
                  "Institute Account Branch",
                  "Institute Bank Account Branch"
                )
              ),
              bankDetails: String(
                getVal(
                  row,
                  "Bank Details",
                  "bankDetails",
                  "Institute Bank Details"
                )
              ),
              ntn: String(
                getVal(row, "NTN", "NTN No", "NTN No.", "Institute NTN")
              ),
              attachments: String(getVal(row, "Attachments", "attachments")),
              status: "active",
            };
          })
          .filter((item) => item.branch !== "" || item.instituteName !== "");

        this.$bvToast.toast(
          `${this.importData.length} ${this.$t("records read from Excel")}`,
          {
            title: this.$t("File Loaded"),
            variant: "info",
            toaster: "b-toaster-top-center",
          }
        );
      };

      reader.readAsArrayBuffer(file);
      this.$refs.bulkFileInput.value = "";
    },

    onBulkSidebarHidden() {
      this.stopPolling();
      this.jobID = null;
      this.jobStatus = null;
      this.importData = [];
    },

    stopPolling() {
      if (this.pollTimer) {
        clearInterval(this.pollTimer);
        this.pollTimer = null;
      }
      this.importLoading = false;
    },

    async startPolling() {
      this.stopPolling();
      await this.pollJobStatus();
      this.pollTimer = setInterval(() => {
        this.pollJobStatus();
      }, 2000);
    },

    async pollJobStatus() {
      if (!this.jobID) return;
      const res = await this.get({
        url:
          this.$store.state.domain +
          "BackgroundJobs/GetJobStatus/" +
          this.jobID +
          "?db=" +
          this.$store.state.userData.db,
        token: this.$store.state.userData.token,
        returnData: true,
        context: this,
      });
      console.log("job status response ", res);

      if (res === false || res == null) {
        console.log("Unable to fetch job status.");
        this.$bvToast.toast(this.$t("Upload failed."), {
          title: this.$t("Error") + "!",
          variant: "danger",
          toaster: "b-toaster-top-center",
        });
        this.stopPolling();
        this.jobID = null;
        this.jobStatus = null;
        return;
      }

      if (res.status === "error" || res.status === "failed") {
        this.jobStatus = res;
        console.log("Job failed.");
        this.$bvToast.toast(this.$t("Upload failed."), {
          title: this.$t("Error") + "!",
          variant: "danger",
          toaster: "b-toaster-top-center",
        });
        this.stopPolling();
        this.jobID = null;
        this.jobStatus = null;
        return;
      }

      if (res.status === "notFound") {
        console.log("Job not found.");
        this.$bvToast.toast(this.$t("Upload failed."), {
          title: this.$t("Error") + "!",
          variant: "warning",
          toaster: "b-toaster-top-center",
        });
        this.stopPolling();
        this.jobID = null;
        this.jobStatus = null;
        return;
      }

      this.jobStatus = res;
      const pct = Number(res.percent);
      const total = Number(res.total);
      const processed = Number(res.processed);
      const done =
        res.status == "succeeded" ||
        (!Number.isNaN(pct) && pct >= 100) ||
        (total > 0 && !Number.isNaN(processed) && processed >= total);

      if (done) {
        this.$bvToast.toast(this.$t("Branches imported successfully."), {
          title: this.$t("Success"),
          variant: "success",
          toaster: "b-toaster-top-center",
        });
        this.stopPolling();
        this.jobID = null;
        this.jobStatus = null;
        this.importData = [];
        this.sidebarVisible = false;
        this.LoadData();
      }
    },

    async importNow() {
      if (this.importData.length === 0) return;
      this.importLoading = true;

      const response = await this.post({
        url:
          this.$store.state.domain +
          "CampusDetails/BulkUpload?db=" +
          this.$store.state.userData.db +
          "&userID=" +
          this.$store.state.userData.userID,
        body: this.importData,
        context: this,
        isBulkUpload: true,
        token: this.$store.state.userData.token,
      });
      console.log("import now response ", response);
      this.importLoading = false;

      if (!response) {
        this.$bvToast.toast(this.$t("Upload failed."), {
          title: this.$t("Error") + "!",
          variant: "danger",
          toaster: "b-toaster-top-center",
        });
        return;
      }

      if (response.status === "error") {
        let invalid = 0;
        if (Array.isArray(response.data)) {
          this.invalidRecords = response.data;
          invalid = this.invalidRecords.length;
        }

        const baseMsg = this.$t("Validation failed.");
        const msg =
          invalid > 0
            ? `${baseMsg} ${invalid} ${this.$t("invalid records")}`
            : baseMsg;

        this.$bvToast.toast(msg, {
          title: this.$t("Error") + "!",
          variant: "danger",
          toaster: "b-toaster-top-center",
        });
        return;
      }

      const jid =
        response.jobID ||
        response.jobId ||
        (response.data && (response.data.jobID || response.data.jobId));

      if (!jid) {
        console.log("No job ID returned from server.");
        this.$bvToast.toast(this.$t("Upload failed."), {
          title: this.$t("Error") + "!",
          variant: "danger",
          toaster: "b-toaster-top-center",
        });
        return;
      }

      this.jobID = jid;
      this.jobStatus = null;
      this.startPolling();
    },

    async LoadData() {
      const reqId = ++this.campusLoadSeq;
      this.dataLoading = true;
      this.loadError = false;
      this.logoLoadFailed = {};
      try {
        if (this.canViewCompanies) {
          await this.loadCompaniesMap();
        }
        if (reqId !== this.campusLoadSeq) return;

        let list = await this.fetchCampusListFromApi();
        if (reqId !== this.campusLoadSeq) return;

        if (!list.length) {
          list = await this.fetchCampusListFromApi();
          if (reqId !== this.campusLoadSeq) return;
        }

        if (!list.length) {
          if (this.items.length) {
            this.loadError = true;
            this.$bvToast.toast(
              this.$t("Could not refresh campuses. Showing previous list."),
              {
                title: this.$t("Warning"),
                variant: "warning",
                toaster: "b-toaster-top-center",
              }
            );
            return;
          }
          this.loadError = false;
          this.items = [];
          return;
        }

        this.applyCampusItems(list);
      } catch (e) {
        console.error("LoadData campuses failed:", e);
        if (reqId !== this.campusLoadSeq) return;
        if (this.items.length) {
          this.loadError = true;
          this.$bvToast.toast(
            this.$t("Could not refresh campuses. Showing previous list."),
            {
              title: this.$t("Warning"),
              variant: "warning",
              toaster: "b-toaster-top-center",
            }
          );
        } else {
          this.loadError = true;
          this.items = [];
        }
      } finally {
        if (reqId === this.campusLoadSeq) {
          this.dataLoading = false;
        }
      }
    },
    async LoadSettings() {
      if (!this.isTcfDomain) return;

      let res = await this.post({
        url:
          this.$store.state.domain +
          "Settings/getSettings?db=" +
          this.$store.state.userData.db +
          "&cID=" +
          this.$store.state.userData.cId,
        body: ["head_office"],
        context: this,
        token: this.$store.state.userData.token,
        subjects: true,
      });

      const headOfficeObj = res.find((el) => el.key == "head_office");
      this.isHeadOffice =
        headOfficeObj &&
        headOfficeObj.campusID == this.$store.state.userData.cId;
    },
  },
};
</script>

<style lang="scss" scoped>
.per-page-selector {
  width: 90px;
}

.branch-cards-page {
  padding: 0.75rem 0 1.75rem;
}

.branch-page-intro {
  display: flex;
  align-items: center;
  gap: 0.85rem;
  margin-bottom: 1.35rem;
  padding: 0 0.15rem;

  &__icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 2.75rem;
    height: 2.75rem;
    border-radius: 14px;
    color: #fff;
    background: linear-gradient(135deg, #7367f0 0%, #6254e8 100%);
    box-shadow: 0 6px 18px rgba(115, 103, 240, 0.3);
    flex-shrink: 0;
  }

  &__title {
    font-size: 1.25rem;
    font-weight: 800;
    color: #2f2b3d;
    letter-spacing: -0.02em;
  }

  &__sub {
    font-size: 0.88rem;
    color: #6e6b7b;
    margin-top: 0.15rem;
  }

  &__count {
    margin-left: auto;
    font-weight: 700;
    flex-shrink: 0;
    font-size: 0.8125rem;
    padding: 0.45rem 0.85rem;
  }
}

@media (max-width: 767px) {
  .branch-page-intro {
    flex-wrap: wrap;

    &__count {
      margin-left: 0;
      margin-top: 0.25rem;
    }
  }
}

.branch-toolbar-card {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 1rem;
  padding: 0.85rem 1rem;
  background: #fff;
  border: 1px solid #ebe9f1;
  border-radius: 14px;
  box-shadow: 0 4px 18px rgba(34, 41, 47, 0.05);
}

.branch-toolbar {
  display: none;
}

.branch-toolbar__actions {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.65rem;
}

.branch-add-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.55rem;
  border: 0;
  border-radius: 12px;
  padding: 0.65rem 1.15rem;
  font-size: 0.92rem;
  font-weight: 700;
  letter-spacing: 0.01em;
  color: #fff;
  background: linear-gradient(135deg, #7367f0 0%, #6254e8 100%);
  box-shadow: 0 8px 20px rgba(115, 103, 240, 0.35);
  transition: transform 0.15s ease, box-shadow 0.15s ease;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 10px 24px rgba(115, 103, 240, 0.42);
  }

  &:active {
    transform: translateY(0);
  }

  &__icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 1.65rem;
    height: 1.65rem;
    border-radius: 8px;
    background: rgba(255, 255, 255, 0.18);
  }
}

.branch-secondary-btn {
  border-radius: 12px !important;
  font-weight: 600;
  border-color: #ebe9f1 !important;
  color: #5e5873 !important;
  min-height: 44px;
}

.branch-search-field {
  position: relative;
  flex: 1 1 280px;
  max-width: 380px;
  margin-left: auto;

  &__icon {
    position: absolute;
    left: 0.95rem;
    top: 50%;
    transform: translateY(-50%);
    color: #a8aaae;
    pointer-events: none;
  }

  &__input {
    width: 100%;
    min-height: 46px;
    padding: 0.55rem 1rem 0.55rem 2.65rem;
    border: 1px solid #e4e6ef;
    border-radius: 12px;
    background: #f8f8fb;
    font-size: 0.9rem;
    font-weight: 600;
    color: #4b465c;
    transition: border-color 0.15s ease, background 0.15s ease,
      box-shadow 0.15s ease;

    &::placeholder {
      color: #b9b9c3;
      font-weight: 500;
    }

    &:focus {
      outline: none;
      background: #fff;
      border-color: #7367f0;
      box-shadow: 0 0 0 3px rgba(115, 103, 240, 0.15);
    }
  }
}

.branch-search-group {
  display: none;
}

.branch-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 1.35rem;

  &__col {
    min-width: 0;
  }
}

.branch-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 220px;
  color: #6e6b7b;
}

.campus-card {
  position: relative;
  background: #fff;
  border: 1px solid #ebe9f1;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 2px 10px rgba(34, 41, 47, 0.06);
  transition: box-shadow 0.22s ease, transform 0.22s ease;
  height: 100%;
  display: flex;
  flex-direction: column;

  &--clickable {
    cursor: pointer;

    &:hover {
      box-shadow: 0 14px 32px rgba(34, 41, 47, 0.12);
      transform: translateY(-3px);
    }
  }

  &__cover-wrap {
    position: relative;
    height: 108px;
    overflow: hidden;
    background: #eef0f8;
  }

  &__cover {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }

  &__cover-shade {
    position: absolute;
    inset: 0;
    background: linear-gradient(
      180deg,
      rgba(255, 255, 255, 0) 35%,
      rgba(255, 255, 255, 0.92) 100%
    );
    pointer-events: none;
  }

  &__code-badge {
    position: absolute;
    top: 0.55rem;
    left: 0.55rem;
    z-index: 2;
    padding: 0.2rem 0.55rem;
    border-radius: 8px;
    font-size: 0.72rem;
    font-weight: 800;
    letter-spacing: 0.04em;
    color: #fff;
    background: rgba(115, 103, 240, 0.92);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  }

  &__body {
    position: relative;
    padding: 0 1.15rem 1.1rem;
    flex: 1;
    display: flex;
    flex-direction: column;
    margin-top: -1.75rem;
  }

  &__header {
    display: flex;
    align-items: flex-end;
    gap: 0.85rem;
    margin-bottom: 0.85rem;
  }

  &__logo-wrap {
    flex-shrink: 0;
    width: 5.25rem;
    height: 5.25rem;
    border-radius: 50%;
    border: 4px solid #fff;
    background: #fff;
    box-shadow: 0 6px 18px rgba(34, 41, 47, 0.14);
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;
    padding: 0.45rem;
  }

  &__logo-img {
    width: 100%;
    height: 100%;
    object-fit: contain;
    display: block;
  }

  &__logo-fallback {
    font-size: 1rem;
    font-weight: 800;
    color: #7367f0;
    letter-spacing: 0.04em;
  }

  &__titles {
    min-width: 0;
    padding-bottom: 0.15rem;
  }

  &__branch {
    margin: 0;
    font-size: 1.12rem;
    font-weight: 800;
    color: #2f2b3d;
    line-height: 1.25;
    letter-spacing: -0.01em;
  }

  &__org {
    margin: 0.2rem 0 0;
    font-size: 0.84rem;
    font-weight: 700;
    color: #5e5873;
  }

  &__meta {
    list-style: none;
    margin: 0;
    padding: 0.65rem 0 0;
    flex: 1;
    border-top: 1px solid #f1f0f5;

    li {
      display: flex;
      align-items: flex-start;
      gap: 0.65rem;
      margin-bottom: 0.65rem;

      &:last-child {
        margin-bottom: 0;
      }
    }
  }

  &__meta-icon {
    flex-shrink: 0;
    width: 2rem;
    height: 2rem;
    border-radius: 10px;
    background: #f3f2f7;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    color: #7367f0;
  }

  &__meta-text {
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 0.1rem;
  }

  &__meta-label {
    font-size: 0.68rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    color: #a8aaae;
    line-height: 1.2;
  }

  &__meta-value {
    font-size: 0.86rem;
    font-weight: 700;
    color: #3f3a4f;
    line-height: 1.35;
    word-break: break-word;
  }

  &__footer {
    display: flex;
    justify-content: flex-end;
    margin-top: 0.85rem;
    padding-top: 0.35rem;
  }

  &__edit {
    width: 2.25rem;
    height: 2.25rem;
    border-color: #ebe9f1 !important;
    background: #fff !important;
    box-shadow: 0 2px 8px rgba(34, 41, 47, 0.08);
  }
}

.campus-card--skeleton {
  pointer-events: none;

  .campus-card__body {
    margin-top: -1.75rem;
  }

  .campus-card__cover-wrap {
    background: #eef0f8;
  }

  .skeleton-block--flat {
    width: 100%;
    height: 100%;
    border-radius: 0;
  }

  .campus-card__logo-wrap {
    padding: 0;
    overflow: hidden;
  }

  .skeleton-block--logo {
    width: 100%;
    height: 100%;
    border-radius: 50%;
  }

  .campus-card__titles--skeleton {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 0.45rem;
    padding-bottom: 0.15rem;
  }

  .skeleton-meta-col {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 0.35rem;
  }

  .skeleton-block--icon {
    width: 2rem;
    height: 2rem;
    border-radius: 10px;
    flex-shrink: 0;
  }

  .skeleton-block--edit {
    width: 2.25rem;
    height: 2.25rem;
    border-radius: 50%;
  }

  .campus-card__meta li:nth-child(1) .skeleton-line--value {
    width: 70%;
  }
  .campus-card__meta li:nth-child(2) .skeleton-line--value {
    width: 82%;
  }
  .campus-card__meta li:nth-child(3) .skeleton-line--value {
    width: 58%;
  }
  .campus-card__meta li:nth-child(4) .skeleton-line--value {
    width: 92%;
  }
}

@keyframes skeleton-shimmer {
  0% {
    background-position: -200% 0;
  }
  100% {
    background-position: 200% 0;
  }
}

.skeleton-block,
.skeleton-line,
.skeleton-circle {
  background: linear-gradient(
    90deg,
    #eceef4 0%,
    #f6f7fb 45%,
    #eceef4 90%
  );
  background-size: 200% 100%;
  animation: skeleton-shimmer 1.35s ease-in-out infinite;
}

.skeleton-line {
  height: 12px;
  border-radius: 6px;

  &--title {
    width: 85%;
    height: 16px;
    margin: 0;
  }

  &--sub {
    width: 62%;
    margin: 0;
  }

  &--label {
    width: 38%;
    height: 9px;
    margin: 0;
  }

  &--value {
    width: 78%;
    height: 12px;
    margin: 0;
  }
}
</style>

<style lang="scss">
@import "@core/scss/vue/libs/vue-select.scss";
@import "@core/scss/vue/libs/vue-flatpicker.scss";
</style>
